<template>
  <div class="pag-permisos">
    <!-- CABECERA -->
    <div class="cabecera">
      <div class="cabecera__izquierda">
        <div class="cabecera__icono">
          <i class="bi bi-shield-lock-fill"></i>
        </div>
        <div>
          <h1 class="cabecera__titulo">Permisos por Empresa</h1>
          <p class="cabecera__subtitulo">Asigna módulos disponibles a cada empresa del sistema</p>
        </div>
      </div>
      <div class="cabecera__acciones">
        <button class="btn-secundario" @click="recargar" :disabled="cargando">
          <i class="bi bi-arrow-clockwise" :class="{ 'girando': cargando }"></i>
          Actualizar
        </button>
      </div>
    </div>

    <!-- SELECTOR DE EMPRESA + BÚSQUEDA -->
    <div class="panel-filtros">
      <div class="panel-filtros__empresa">
        <label class="etiqueta-campo">Empresa</label>
        <select v-model="empresaId" @change="cargarModulosEmpresa" class="select-empresa">
          <option value="">— Selecciona una empresa —</option>
          <option v-for="emp in empresas" :key="emp.id" :value="emp.id">
            {{ emp.business_name || emp.razon_social }} ({{ emp.ruc }})
          </option>
        </select>
      </div>
      <div class="panel-filtros__busqueda" v-if="empresaId">
        <i class="bi bi-search panel-filtros__icono"></i>
        <input
          v-model="busqueda"
          type="text"
          placeholder="Filtrar módulos..."
          class="panel-filtros__campo"
        />
      </div>
      <div class="panel-filtros__acciones" v-if="empresaId">
        <button class="btn-secundario" @click="seleccionarTodos">
          <i class="bi bi-check2-all"></i>
          Activar todos
        </button>
        <button class="btn-secundario" @click="deseleccionarTodos">
          <i class="bi bi-x-circle"></i>
          Desactivar todos
        </button>
        <button class="btn-primario" @click="guardarCambios" :disabled="guardando">
          <span v-if="guardando" class="mini-spinner"></span>
          <i v-else class="bi bi-floppy"></i>
          Guardar cambios
        </button>
      </div>
    </div>

    <!-- ESTADO: SIN EMPRESA -->
    <div v-if="!empresaId" class="estado-vacio">
      <i class="bi bi-building estado-vacio__icono"></i>
      <p>Selecciona una empresa para gestionar sus permisos de módulos</p>
    </div>

    <!-- ESTADO: CARGANDO -->
    <div v-else-if="cargando" class="estado-carga">
      <div class="spinner"></div>
      <span>Cargando módulos...</span>
    </div>

    <!-- ESTADO: ERROR -->
    <div v-else-if="error" class="estado-error">
      <i class="bi bi-exclamation-triangle-fill"></i>
      {{ error }}
    </div>

    <!-- RESUMEN + MÓDULOS -->
    <template v-else-if="modulosSistema.length">
      <!-- Resumen de la empresa -->
      <div class="resumen">
        <div class="resumen__dato">
          <span class="resumen__numero resumen__numero--azul">{{ totalActivos }}</span>
          <span class="resumen__etiqueta">Módulos activos</span>
        </div>
        <div class="resumen__dato">
          <span class="resumen__numero">{{ totalSecundarios }}</span>
          <span class="resumen__etiqueta">Total disponibles</span>
        </div>
        <div class="resumen__barra">
          <div
            class="resumen__barra-relleno"
            :style="{ width: totalSecundarios ? (totalActivos / totalSecundarios * 100) + '%' : '0%' }"
          ></div>
        </div>
      </div>

      <!-- GRUPOS POR MÓDULO PRINCIPAL -->
      <div class="grupos">
        <div
          v-for="principal in modulosFiltrados"
          :key="principal.id"
          class="grupo"
        >
          <div class="grupo__encabezado">
            <div class="grupo__icono">
              <i :class="principal.icono || 'bi bi-puzzle'"></i>
            </div>
            <div class="grupo__info">
              <span class="grupo__nombre">{{ principal.nombre }}</span>
            </div>
            <div class="grupo__contador">
              <span class="contador">
                {{ principal.submodulos.filter(s => modulosActivos.has(s.id)).length }}
                / {{ principal.submodulos.length }}
              </span>
            </div>
            <div class="grupo__accion-rapida">
              <button class="btn-mini" @click="toggleGrupo(principal, true)" title="Activar todos">
                <i class="bi bi-check-all"></i>
              </button>
              <button class="btn-mini" @click="toggleGrupo(principal, false)" title="Desactivar todos">
                <i class="bi bi-dash-circle"></i>
              </button>
            </div>
          </div>

          <div class="grupo__submodulos">
            <label
              v-for="sub in principal.submodulos"
              :key="sub.id"
              class="submodulo"
              :class="{ 'submodulo--activo': modulosActivos.has(sub.id) }"
            >
              <input
                type="checkbox"
                class="submodulo__check"
                :checked="modulosActivos.has(sub.id)"
                @change="toggleSubmodulo(sub.id)"
              />
              <div class="submodulo__contenido">
                <span class="submodulo__nombre">{{ sub.nombre }}</span>
                <span class="submodulo__ruta">{{ sub.ruta_modulo_secundario || '' }}</span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <!-- BOTÓN GUARDAR FLOTANTE -->
      <div class="guardar-flotante" v-if="hayCambiosPendientes">
        <span class="guardar-flotante__texto">Tienes cambios sin guardar</span>
        <button class="btn-primario" @click="guardarCambios" :disabled="guardando">
          <span v-if="guardando" class="mini-spinner"></span>
          <i v-else class="bi bi-floppy"></i>
          Guardar cambios
        </button>
      </div>
    </template>

    <!-- VACÍO -->
    <div v-else class="estado-vacio">
      <i class="bi bi-puzzle estado-vacio__icono"></i>
      <p>No hay módulos configurados en el sistema. Ejecuta el seed de módulos.</p>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import notificationService from '@/services/notificationService'

export default {
  name: 'ModulosPermisos',

  data() {
    return {
      empresas: [],
      empresaId: '',
      modulosSistema: [],
      modulosActivos: new Set(),
      modulosActivosOriginal: new Set(),
      cargando: false,
      guardando: false,
      error: '',
      busqueda: '',
    }
  },

  computed: {
    totalSecundarios() {
      return this.modulosSistema.reduce((acc, mp) => acc + mp.submodulos.length, 0)
    },
    totalActivos() {
      return this.modulosActivos.size
    },
    modulosFiltrados() {
      if (!this.busqueda.trim()) return this.modulosSistema
      const q = this.busqueda.toLowerCase()
      return this.modulosSistema
        .map(mp => ({
          ...mp,
          submodulos: mp.submodulos.filter(s =>
            s.nombre?.toLowerCase().includes(q) ||
            (s.ruta_modulo_secundario || '').toLowerCase().includes(q)
          ),
        }))
        .filter(mp => mp.nombre?.toLowerCase().includes(q) || mp.submodulos.length > 0)
    },
    hayCambiosPendientes() {
      if (this.modulosActivos.size !== this.modulosActivosOriginal.size) return true
      for (const id of this.modulosActivos) {
        if (!this.modulosActivosOriginal.has(id)) return true
      }
      return false
    },
  },

  mounted() {
    this.cargarEmpresas()
  },

  methods: {
    async cargarEmpresas() {
      try {
        const { data } = await api.get('/admin/empresas')
        this.empresas = data?.data || data?.empresas || []
      } catch (e) {
        console.error('[ModulosPermisos]', e)
        notificationService.error('No se pudieron cargar las empresas')
      }
    },

    async cargarModulosEmpresa() {
      if (!this.empresaId) {
        this.modulosSistema = []
        this.modulosActivos = new Set()
        this.modulosActivosOriginal = new Set()
        return
      }
      this.cargando = true
      this.error = ''
      try {
        const { data } = await api.get(`/admin/empresas/${this.empresaId}/modulos`)
        const lista = data?.data || []
        this.modulosSistema = lista

        const activos = new Set()
        for (const mp of lista) {
          for (const sub of mp.submodulos || []) {
            if (sub.activo_empresa) activos.add(sub.id)
          }
        }
        this.modulosActivos = activos
        this.modulosActivosOriginal = new Set(activos)
      } catch (e) {
        this.error = 'Error al cargar los módulos de la empresa'
        console.error('[ModulosPermisos]', e)
      } finally {
        this.cargando = false
      }
    },

    toggleSubmodulo(id) {
      const nuevo = new Set(this.modulosActivos)
      if (nuevo.has(id)) {
        nuevo.delete(id)
      } else {
        nuevo.add(id)
      }
      this.modulosActivos = nuevo
    },

    toggleGrupo(principal, activar) {
      const nuevo = new Set(this.modulosActivos)
      for (const sub of principal.submodulos) {
        if (activar) {
          nuevo.add(sub.id)
        } else {
          nuevo.delete(sub.id)
        }
      }
      this.modulosActivos = nuevo
    },

    seleccionarTodos() {
      const todos = new Set()
      for (const mp of this.modulosSistema) {
        for (const sub of mp.submodulos || []) todos.add(sub.id)
      }
      this.modulosActivos = todos
    },

    deseleccionarTodos() {
      this.modulosActivos = new Set()
    },

    async guardarCambios() {
      this.guardando = true
      try {
        await api.put(`/admin/empresas/${this.empresaId}/modulos`, {
          modulos: Array.from(this.modulosActivos),
        })
        notificationService.success('Módulos actualizados correctamente')
        await this.cargarModulosEmpresa()
      } catch (e) {
        notificationService.error('Error al guardar los cambios')
        console.error('[ModulosPermisos]', e)
      } finally {
        this.guardando = false
      }
    },

    recargar() {
      this.cargarEmpresas()
      if (this.empresaId) this.cargarModulosEmpresa()
    },
  },
}
</script>

<style scoped>
.pag-permisos {
  --primario: var(--primary-main, #0ea5e9);
  --primario-hover: var(--primary-hover, #0284c7);
  --fondo: var(--bg-main, #f8fafc);
  --fondo-tarjeta: var(--secondary-bg-main, #ffffff);
  --texto: var(--text-primary, #0f172a);
  --texto-sec: var(--text-secondary, #475569);
  --texto-mut: var(--text-muted, #94a3b8);
  --borde: var(--border-main, #e2e8f0);
  --borde-foco: var(--border-focus, #0ea5e9);
  --exito: var(--accent-success, #10b981);
  --alerta: var(--accent-warning, #f59e0b);
  --peligro: var(--accent-danger, #ef4444);
  --sombra-sm: var(--shadow-sm, 0 1px 2px 0 rgb(0 0 0 / 0.05));
  --sombra-md: var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1));
  min-height: 100vh;
  padding: 1.5rem;
  background: var(--fondo);
  color: var(--texto);
  padding-bottom: 6rem;
}

/* CABECERA */
.cabecera { display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
.cabecera__izquierda { display: flex; align-items: center; gap: 0.875rem; }
.cabecera__icono { width: 2.75rem; height: 2.75rem; border-radius: 10px; background: var(--primario); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; flex-shrink: 0; }
.cabecera__titulo { margin: 0; font-size: 1.375rem; font-weight: 700; color: var(--texto); line-height: 1.2; }
.cabecera__subtitulo { margin: 0.125rem 0 0; font-size: 0.8125rem; color: var(--texto-sec); }
.cabecera__acciones { display: flex; gap: 0.625rem; }

/* BOTONES */
.btn-primario { display: inline-flex; align-items: center; gap: 0.375rem; padding: 0.5rem 1rem; background: var(--primario); color: #fff; border: none; border-radius: 8px; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: background 0.15s, transform 0.1s; }
.btn-primario:hover:not(:disabled) { background: var(--primario-hover); transform: translateY(-1px); }
.btn-primario:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secundario { display: inline-flex; align-items: center; gap: 0.375rem; padding: 0.5rem 1rem; background: var(--fondo-tarjeta); color: var(--texto-sec); border: 1px solid var(--borde); border-radius: 8px; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: background 0.15s; }
.btn-secundario:hover:not(:disabled) { background: var(--borde); color: var(--texto); }
.btn-mini { width: 1.75rem; height: 1.75rem; border: 1px solid var(--borde); border-radius: 6px; background: transparent; color: var(--texto-mut); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 0.875rem; transition: background 0.15s, color 0.15s; }
.btn-mini:hover { background: var(--borde); color: var(--texto-sec); }

/* PANEL FILTROS */
.panel-filtros { display: flex; gap: 0.75rem; margin-bottom: 1.5rem; flex-wrap: wrap; align-items: flex-end; padding: 1.125rem; background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-radius: 10px; box-shadow: var(--sombra-sm); }
.panel-filtros__empresa { display: flex; flex-direction: column; gap: 0.375rem; flex: 1; min-width: 240px; }
.etiqueta-campo { font-size: 0.8125rem; font-weight: 500; color: var(--texto-sec); }
.select-empresa { padding: 0.5625rem 0.75rem; border: 1px solid var(--borde); border-radius: 8px; background: var(--fondo); color: var(--texto); font-size: 0.875rem; cursor: pointer; }
.select-empresa:focus { outline: none; border-color: var(--borde-foco); box-shadow: 0 0 0 3px rgb(14 165 233 / 0.15); }
.panel-filtros__busqueda { position: relative; flex: 1; min-width: 180px; }
.panel-filtros__icono { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--texto-mut); pointer-events: none; font-size: 0.875rem; }
.panel-filtros__campo { width: 100%; padding: 0.5625rem 0.875rem 0.5625rem 2.25rem; border: 1px solid var(--borde); border-radius: 8px; background: var(--fondo); color: var(--texto); font-size: 0.875rem; }
.panel-filtros__campo:focus { outline: none; border-color: var(--borde-foco); box-shadow: 0 0 0 3px rgb(14 165 233 / 0.15); }
.panel-filtros__campo::placeholder { color: var(--texto-mut); }
.panel-filtros__acciones { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }

/* RESUMEN */
.resumen { display: flex; align-items: center; gap: 1.5rem; padding: 1rem 1.25rem; background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-radius: 10px; margin-bottom: 1.25rem; box-shadow: var(--sombra-sm); flex-wrap: wrap; }
.resumen__dato { display: flex; flex-direction: column; align-items: center; }
.resumen__numero { font-size: 1.5rem; font-weight: 700; color: var(--texto); line-height: 1; }
.resumen__numero--azul { color: var(--primario); }
.resumen__etiqueta { font-size: 0.6875rem; color: var(--texto-mut); text-transform: uppercase; letter-spacing: 0.04em; margin-top: 0.25rem; }
.resumen__barra { flex: 1; min-width: 120px; height: 8px; background: var(--borde); border-radius: 4px; overflow: hidden; }
.resumen__barra-relleno { height: 100%; background: var(--primario); border-radius: 4px; transition: width 0.4s ease; }

/* GRUPOS */
.grupos { display: grid; gap: 1rem; }

.grupo { background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-radius: 10px; overflow: hidden; box-shadow: var(--sombra-sm); }

.grupo__encabezado { display: flex; align-items: center; gap: 0.75rem; padding: 0.875rem 1rem; border-bottom: 1px solid var(--borde); background: rgb(14 165 233 / 0.04); }
.grupo__icono { width: 2rem; height: 2rem; border-radius: 8px; background: rgb(14 165 233 / 0.1); color: var(--primario); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.grupo__info { flex: 1; min-width: 0; }
.grupo__nombre { font-size: 0.9375rem; font-weight: 600; color: var(--texto); }
.grupo__contador { flex-shrink: 0; }
.contador { font-size: 0.8125rem; font-weight: 600; color: var(--primario); padding: 0.1875rem 0.625rem; background: rgb(14 165 233 / 0.1); border-radius: 20px; }
.grupo__accion-rapida { display: flex; gap: 0.25rem; }

.grupo__submodulos { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 0; }

.submodulo { display: flex; align-items: flex-start; gap: 0.625rem; padding: 0.625rem 1rem; cursor: pointer; border-right: 1px solid rgb(0 0 0 / 0.04); border-bottom: 1px solid rgb(0 0 0 / 0.04); transition: background 0.1s; }
.submodulo:hover { background: rgb(14 165 233 / 0.04); }
.submodulo--activo { background: rgb(16 185 129 / 0.05); }
.submodulo--activo .submodulo__nombre { color: var(--exito); }
.submodulo__check { margin-top: 0.125rem; accent-color: var(--exito); width: 0.9375rem; height: 0.9375rem; flex-shrink: 0; cursor: pointer; }
.submodulo__contenido { display: flex; flex-direction: column; gap: 0.0625rem; min-width: 0; }
.submodulo__nombre { font-size: 0.8125rem; font-weight: 500; color: var(--texto); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.submodulo__ruta { font-size: 0.6875rem; color: var(--texto-mut); font-family: monospace; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ESTADOS */
.estado-carga { display: flex; align-items: center; justify-content: center; gap: 0.75rem; padding: 3rem; color: var(--texto-sec); }
.estado-error { display: flex; align-items: center; gap: 0.625rem; padding: 1rem 1.25rem; background: rgb(239 68 68 / 0.08); border: 1px solid var(--peligro); border-radius: 8px; color: var(--peligro); font-size: 0.875rem; }
.estado-vacio { text-align: center; padding: 4rem 2rem; color: var(--texto-sec); }
.estado-vacio__icono { font-size: 3rem; color: var(--texto-mut); display: block; margin-bottom: 1rem; }

/* GUARDAR FLOTANTE */
.guardar-flotante { position: fixed; bottom: 1.5rem; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 1rem; padding: 0.75rem 1.25rem; background: var(--fondo-tarjeta); border: 1px solid var(--borde); border-radius: 40px; box-shadow: 0 8px 30px rgb(0 0 0 / 0.12); z-index: 100; }
.guardar-flotante__texto { font-size: 0.875rem; color: var(--texto-sec); white-space: nowrap; }

/* SPINNER */
.spinner { width: 1.5rem; height: 1.5rem; border: 2px solid var(--borde); border-top-color: var(--primario); border-radius: 50%; animation: girar 0.7s linear infinite; }
.mini-spinner { width: 0.875rem; height: 0.875rem; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: girar 0.6s linear infinite; display: inline-block; }

/* ANIMACIONES */
@keyframes girar { to { transform: rotate(360deg); } }
.girando { animation: girar 0.7s linear infinite; }

/* RESPONSIVE */
@media (max-width: 768px) {
  .pag-permisos { padding: 1rem; }
  .cabecera { flex-direction: column; align-items: flex-start; }
  .grupo__submodulos { grid-template-columns: 1fr; }
  .guardar-flotante { width: calc(100% - 2rem); justify-content: space-between; }
}
</style>
